import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtNum, fmtDate, fileUrl, errMsg } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { EmptyState } from "@finance/components/DesignSystem";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Label } from "@finance/components/ui/label";
import { DateField } from "@finance/components/DateField";
import { SearchableSelect } from "@finance/components/SearchableSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@finance/components/ui/dialog";
import { CaretDown, CaretRight, Paperclip, Package } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ReverseActions } from "@finance/components/ReverseActions";

const statusBadge = (l) => {
  const st = l.status || "unpaid";
  const map = {
    paid: "bg-emerald-50 text-emerald-700",
    partial: "bg-amber-50 text-amber-700",
    unpaid: "bg-gray-100 text-gray-600",
    voided: "bg-red-50 text-red-600 line-through",
  };
  return <span className={`ml-2 rounded-sm px-1.5 py-0.5 text-[10px] font-medium ${map[st] || map.unpaid}`} data-testid={`lodge-status-${l.id}`}>{st}</span>;
};

export default function LodgmentHistory() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState({});
  const [detail, setDetail] = useState({});
  const [banks, setBanks] = useState([]);
  const [params, setParams] = useSearchParams();
  const [payFor, setPayFor] = useState(null);
  const [pf, setPf] = useState({});
  const [voidFor, setVoidFor] = useState(null);
  const [vReason, setVReason] = useState("");

  const load = () => api.get("/asset-lodgments").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); api.get("/bank-accounts").then((r) => setBanks(r.data)).catch(() => {}); }, []);

  const fetchDetail = async (id) => {
    try { const { data } = await api.get(`/asset-lodgments/${id}`); setDetail((d) => ({ ...d, [id]: data })); } catch { /* ignore */ }
  };

  // Auto-expand a lodgment when arriving via ?open=<id> (e.g. from an asset badge).
  useEffect(() => {
    const oid = params.get("open");
    if (oid) { setOpen((o) => ({ ...o, [oid]: true })); fetchDetail(oid); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const toggle = async (id) => {
    const nx = !open[id];
    setOpen((o) => ({ ...o, [id]: nx }));
    if (nx && !detail[id]) fetchDetail(id);
  };

  const openPay = (l) => {
    const outstanding = Math.max((l.total_inclusive || 0) - (l.paid_amount || 0), 0);
    setPayFor(l);
    setPf({ bank_account_id: "", amount: String(outstanding), date: new Date().toISOString().slice(0, 10) });
  };
  const submitPay = async () => {
    try {
      await api.post(`/asset-lodgments/${payFor.id}/pay`, { bank_account_id: pf.bank_account_id, amount: Number(pf.amount), date: pf.date });
      toast.success("Payment posted — AP settled");
      setPayFor(null); load(); if (open[payFor.id]) fetchDetail(payFor.id);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const submitVoid = async () => {
    try {
      await api.post(`/asset-lodgments/${voidFor.id}/void`, { reason: vReason });
      toast.success("Lodgment voided — GL reversed and assets removed");
      setVoidFor(null); setVReason(""); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const reversePayment = async (l, p) => {
    if (!window.confirm(`Reverse this payment of TSh ${fmtNum(p.amount)}? The balance returns to Payables.`)) return;
    try {
      await api.post(`/asset-lodgments/${l.id}/payments/${p.id}/reverse`);
      toast.success("Payment reversed — AP re-opened");
      load(); if (open[l.id]) fetchDetail(l.id);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Layout>
      <PageHeader title="Lodgment History" subtitle="Past asset lodgments — invoice detail, GL journal, payments, and attachments" />
      <div className="p-8">
        {rows.length === 0 ? (
          <EmptyState icon={Package} title="No lodgments yet" message="Lodge an asset from the Asset Lodgment page to see it here." />
        ) : (
          <div className="mx-auto max-w-5xl space-y-3">
            {rows.map((l) => {
              const d = detail[l.id];
              const outstanding = Math.max((l.total_inclusive || 0) - (l.paid_amount || 0), 0);
              const voided = l.status === "voided";
              return (
                <div key={l.id} className="overflow-hidden rounded-sm border border-gray-200 bg-white" data-testid={`lodge-hist-${l.id}`}>
                  <button className="flex w-full items-center justify-between px-5 py-3 text-left hover:bg-gray-50" data-testid={`lodge-hist-toggle-${l.id}`} onClick={() => toggle(l.id)}>
                    <div className="flex items-center gap-3">
                      {open[l.id] ? <CaretDown size={16} className="text-gray-400" /> : <CaretRight size={16} className="text-gray-400" />}
                      <div>
                        <div className="font-display text-sm font-bold text-gray-900">{l.supplier_name}{statusBadge(l)}</div>
                        <div className="text-xs text-gray-500 lx-num">{fmtDate(l.date)} · {(l.lines || []).length} asset(s){l.vat_applicable ? " · VAT" : ""}{l.due_date ? ` · due ${fmtDate(l.due_date)}` : ""}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="lx-num text-sm font-semibold text-gray-900">TSh {fmtNum(l.total_inclusive)}</div>
                      <div className="text-[11px] text-gray-400">{voided ? "voided" : outstanding > 0.01 ? `TSh ${fmtNum(outstanding)} outstanding` : "paid in full"}</div>
                    </div>
                  </button>
                  {open[l.id] && (
                    <div className="border-t border-gray-100 px-5 py-4 text-sm">
                      <div className="mb-3 grid grid-cols-2 gap-x-8 gap-y-1 text-xs text-gray-600 md:grid-cols-3">
                        <div><span className="text-gray-400">Exclusive:</span> TSh {fmtNum(l.total_exclusive)}</div>
                        <div><span className="text-gray-400">VAT ({l.vat_rate || 0}%):</span> TSh {fmtNum(l.vat_amount)}</div>
                        <div><span className="text-gray-400">Credit days:</span> {l.credit_days ?? 0} (due {fmtDate(l.due_date)})</div>
                        {l.efd_verification_code && <div><span className="text-gray-400">EFD code:</span> {l.efd_verification_code}</div>}
                        {l.efd_timestamp && <div><span className="text-gray-400">EFD time:</span> {l.efd_timestamp}</div>}
                        <div><span className="text-gray-400">Paid:</span> TSh {fmtNum(l.paid_amount || 0)}</div>
                      </div>

                      <div className="lx-label mb-1 text-gray-500">Assets</div>
                      <table className="mb-4 w-full text-xs">
                        <thead><tr className="text-gray-400"><th className="py-1 text-left">Description</th><th className="py-1 text-left">GL</th><th className="py-1 text-right">Exclusive</th></tr></thead>
                        <tbody>
                          {(l.lines || []).map((ln, i) => (
                            <tr key={i} className="border-t border-gray-50"><td className="py-1">{ln.name}</td><td className="py-1 text-gray-500">{ln.account_code}</td><td className="py-1 text-right lx-num">TSh {fmtNum(ln.exclusive)}</td></tr>
                          ))}
                        </tbody>
                      </table>

                      {d?.gl?.lines && (
                        <>
                          <div className="lx-label mb-1 text-gray-500">GL Journal</div>
                          <table className="mb-4 w-full text-xs" data-testid={`lodge-hist-gl-${l.id}`}>
                            <thead><tr className="text-gray-400"><th className="py-1 text-left">Account</th><th className="py-1 text-right">Debit</th><th className="py-1 text-right">Credit</th></tr></thead>
                            <tbody>
                              {d.gl.lines.map((g, i) => (
                                <tr key={i} className="border-t border-gray-50"><td className="py-1">{g.account_code}{g.supplier_id ? " · AP (supplier)" : ""}</td><td className="py-1 text-right lx-num">{g.debit ? fmtNum(g.debit) : "—"}</td><td className="py-1 text-right lx-num">{g.credit ? fmtNum(g.credit) : "—"}</td></tr>
                              ))}
                            </tbody>
                          </table>
                        </>
                      )}

                      {(l.payments || []).length > 0 && (
                        <>
                          <div className="lx-label mb-1 text-gray-500">Payments</div>
                          <table className="mb-4 w-full text-xs" data-testid={`lodge-hist-pay-${l.id}`}>
                            <thead><tr className="text-gray-400"><th className="py-1 text-left">Date</th><th className="py-1 text-left">Bank</th><th className="py-1 text-right">Amount</th><th className="py-1 text-right"></th></tr></thead>
                            <tbody>
                              {l.payments.map((p) => (
                                <tr key={p.id} className="border-t border-gray-50"><td className="py-1">{fmtDate(p.date)}</td><td className="py-1 text-gray-500">{p.bank_name}</td><td className="py-1 text-right lx-num">TSh {fmtNum(p.amount)}</td><td className="py-1 text-right"><button className="text-[11px] text-red-600 hover:underline" data-testid={`lodge-reverse-pay-${p.id}`} onClick={() => reversePayment(l, p)}>reverse</button></td></tr>
                              ))}
                            </tbody>
                          </table>
                        </>
                      )}

                      <div className="lx-label mb-1 text-gray-500">Attachments</div>
                      {(l.documents || []).length === 0 ? (
                        <div className="text-xs text-gray-400">No documents attached.</div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {l.documents.map((doc) => (
                            <a key={doc.id} href={fileUrl(`asset-lodgments/${l.id}/documents/${doc.id}/file`)} target="_blank" rel="noreferrer"
                              className="inline-flex items-center gap-1 rounded-sm border border-gray-200 px-2 py-1 text-xs text-indigo-600 hover:bg-indigo-50" data-testid={`lodge-hist-doc-${doc.id}`}>
                              <Paperclip size={12} />{doc.filename}
                            </a>
                          ))}
                        </div>
                      )}

                      {!voided && (
                        <div className="mt-4 border-t border-gray-100 pt-3"><ReverseActions sourceId={l.id} title="Reverse GL journal" onDone={() => { load(); fetchDetail(l.id); }} /></div>
                      )}
                      {!voided && (
                        <div className="mt-4 flex items-center gap-2 border-t border-gray-100 pt-3">
                          {outstanding > 0.01 && <Button size="sm" className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid={`lodge-pay-${l.id}`} onClick={() => openPay(l)}>Pay TSh {fmtNum(outstanding)}</Button>}
                          <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" data-testid={`lodge-void-${l.id}`} onClick={() => setVoidFor(l)}>Void</Button>
                        </div>
                      )}
                      {voided && <div className="mt-4 border-t border-gray-100 pt-3 text-xs text-red-500" data-testid={`lodge-voided-note-${l.id}`}>Voided by {l.voided_by} on {fmtDate(l.voided_at)}{l.void_reason ? ` — ${l.void_reason}` : ""}</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={!!payFor} onOpenChange={(v) => !v && setPayFor(null)}>
        <DialogContent data-testid="lodge-pay-dialog"><DialogHeader><DialogTitle className="font-display">Pay Lodgment — {payFor?.supplier_name}</DialogTitle><DialogDescription>Settle the Accounts Payable raised by this lodgment.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-sm bg-gray-50 p-3 text-sm"><span className="text-gray-500">Outstanding:</span> <b className="lx-num">TSh {fmtNum(Math.max((payFor?.total_inclusive || 0) - (payFor?.paid_amount || 0), 0))}</b></div>
            <div><Label className="lx-label">Bank account</Label>
              <SearchableSelect className="mt-1" testid="lodge-pay-bank" placeholder="Select bank"
                value={pf.bank_account_id} onValueChange={(v) => setPf({ ...pf, bank_account_id: v })}
                options={banks.map((b) => ({ value: b.id, label: `${b.bank_name}${b.account_no ? ` · ${b.account_no}` : ""}` }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="lx-label">Amount</Label><Input type="number" className="mt-1" data-testid="lodge-pay-amount" value={pf.amount} onChange={(e) => setPf({ ...pf, amount: e.target.value })} /></div>
              <div><Label className="lx-label">Date</Label><DateField className="mt-1" data-testid="lodge-pay-date" value={pf.date} onChange={(e) => setPf({ ...pf, date: e.target.value })} /></div>
            </div>
            <p className="text-xs text-gray-500">Posts Dr Accounts Payable (2000) / Cr Bank. Partial payments leave the balance in Payables.</p>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPayFor(null)}>Cancel</Button><Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="lodge-pay-submit" disabled={!pf.bank_account_id || !(Number(pf.amount) > 0)} onClick={submitPay}>Post Payment</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!voidFor} onOpenChange={(v) => { if (!v) { setVoidFor(null); setVReason(""); } }}>
        <DialogContent data-testid="lodge-void-dialog"><DialogHeader><DialogTitle className="font-display">Void Lodgment — {voidFor?.supplier_name}</DialogTitle><DialogDescription>Reverse the GL journal and remove the assets this lodgment created.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">This reverses the GL journal and removes the {(voidFor?.lines || []).length} asset(s) created by this lodgment. This cannot be undone.</p>
            <div><Label className="lx-label">Reason (optional)</Label><Input className="mt-1" data-testid="lodge-void-reason" value={vReason} onChange={(e) => setVReason(e.target.value)} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => { setVoidFor(null); setVReason(""); }}>Cancel</Button><Button className="bg-red-600 hover:bg-red-700" data-testid="lodge-void-submit" onClick={submitVoid}>Void Lodgment</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
